import {radialProgressiveBlur} from '@remotion/effects/radial-progressive-blur';
import {MacOSCursor} from '@remotion/mac-cursors';
import {Video} from '@remotion/media';
import {
	AbsoluteFill,
	Composition,
	Easing,
	HtmlInCanvas,
	Interactive,
	interpolate,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

const BasicsVirtualizedTimelineInner = ({
	captureStyle,
}: {
	readonly captureStyle: React.CSSProperties | null;
}) => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();

	const screenCapture = (
		<>
			<Video
				name="Screen recording"
				premountFor={fps}
				muted
				effects={
					HtmlInCanvas.isSupported()
						? []
						: [
								radialProgressiveBlur({
									center: [0.11, 0.72],
									width: 0.55,
									height: 1.1,
									start: 0.65,
									startBlur: 0,
									endBlur: 24,
								}),
							]
				}
				src={
					'https://remotion.media/studio-close-ups/basics-showcase/virtualized-timeline-084ed3b34e03.mp4'
				}
				durationInFrames={1189}
				style={{
					position: 'absolute',
				}}
			/>
			<MacOSCursor
				name="Recorded cursor"
				premountFor={fps}
				cursor={interpolate(
					frame,
					[10, 52, 216, 219, 221, 637, 638],
					['auto', 'row-resize', 'auto', 'custom', 'auto', 'custom', 'auto'],
					{
						easing: Easing.step1,
						extrapolateLeft: 'clamp',
						extrapolateRight: 'clamp',
					},
				)}
				customCursor={
					'url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2032%2032%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url(%23shadow)%22%3E%0A%3Cpath%20d%3D%22M7%204.5H11C12.1%204.5%2013%205.4%2013%206.5V25.5C13%2026.6%2012.1%2027.5%2011%2027.5H7M25%204.5H21C19.9%204.5%2019%205.4%2019%206.5V25.5C19%2026.6%2019.9%2027.5%2021%2027.5H25%22%20stroke%3D%22white%22%20stroke-width%3D%225%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M7%204.5H11C12.1%204.5%2013%205.4%2013%206.5V25.5C13%2026.6%2012.1%2027.5%2011%2027.5H7M25%204.5H21C19.9%204.5%2019%205.4%2019%206.5V25.5C19%2026.6%2019.9%2027.5%2021%2027.5H25%22%20stroke%3D%22black%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22shadow%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2232%22%20height%3D%2232%22%20filterUnits%3D%22userSpaceOnUse%22%3E%0A%3CfeDropShadow%20dx%3D%220%22%20dy%3D%221.5%22%20stdDeviation%3D%221%22%20flood-color%3D%22black%22%20flood-opacity%3D%220.35%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E") 12 12, ew-resize'
				}
				style={{
					position: 'absolute',
					left: 0,
					top: 0,
					scale: interpolate(
						frame,
						[0, 69, 204, 1063, 1068],
						[
							3.301835248596021, 2.9716517237364193, 3.301835248596021,
							2.9716517237364193, 3.301835248596021,
						],
						{
							easing: Easing.step1,
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
					translate: interpolate(
						frame,
						[
							10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
							26, 27, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42,
							43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 84, 85, 86,
							87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102,
							103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115,
							116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127, 128,
							129, 130, 131, 132, 133, 134, 135, 136, 137, 138, 139, 140, 141,
							142, 143, 144, 145, 146, 147, 148, 149, 150, 151, 152, 153, 154,
							155, 156, 157, 158, 159, 160, 161, 162, 163, 164, 166, 167, 168,
							169, 170, 171, 172, 173, 174, 175, 176, 177, 178, 179, 180, 181,
							182, 183, 184, 185, 186, 187, 188, 189, 190, 191, 192, 193, 194,
							195, 196, 197, 198, 199, 201, 202, 203, 204, 214, 215, 216, 217,
							218, 219, 220, 221, 222, 223, 224, 225, 226, 227, 228, 229, 230,
							231, 232, 233, 234, 235, 236, 579, 580, 581, 582, 583, 584, 585,
							586, 587, 588, 589, 590, 591, 592, 593, 594, 595, 596, 597, 598,
							599, 600, 601, 602, 603, 604, 605, 606, 607, 608, 609, 610, 611,
							612, 613, 614, 615, 616, 617, 618, 619, 630, 631, 632, 633, 634,
							635, 636, 637, 638, 639, 640, 641, 642, 644, 645, 646, 647, 648,
							649, 650, 651, 652, 653, 654, 655, 656, 657, 658, 659, 660, 661,
							662, 925, 926, 927, 928, 929, 930, 931, 932, 933, 934, 935, 936,
							937, 938, 939, 940, 941, 942, 943, 944, 945, 946, 947, 948, 949,
							950, 951, 952, 953, 954, 955, 956, 957, 958, 959, 960, 961, 962,
							964, 965, 968, 969, 970, 971, 988, 989, 990, 991, 992, 993, 994,
							995, 996, 997, 998, 999, 1000, 1001, 1002, 1003, 1004, 1005, 1006,
							1007, 1008, 1009, 1010, 1011, 1012, 1013, 1014, 1015, 1016, 1017,
							1018, 1019, 1020, 1021, 1022, 1023, 1024, 1025, 1026, 1027, 1028,
							1029, 1030, 1031, 1032, 1033, 1034, 1035, 1036, 1037, 1038, 1039,
							1040, 1041, 1042, 1043, 1044, 1045, 1046, 1047, 1048, 1049, 1050,
							1051, 1052, 1053, 1056, 1057, 1058, 1060, 1061,
						],
						[
							'2255.7596711062542px 1303.502646734798px',
							'2226.8686126810394px 1303.502646734798px',
							'2197.977554255824px 1295.2480586133079px',
							'2014.6096178132864px 1191.3821240158713px',
							'2014.6096178132864px 1191.3821240158713px',
							'1942.3174827805492px 1151.9406701478767px',
							'1353.5847806031509px 854.762599980295px',
							'1331.7101220812024px 847.4624486103522px',
							'1224.0135426836368px 805.7896763907672px',
							'1173.2478257364728px 786.1076428385894px',
							'1146.6783702204268px 774.3964459412255px',
							'1133.4194380502834px 769.9596048259245px',
							'1126.454629322776px 766.4772004621709px',
							'1121.7082411529193px 765.2132166560677px',
							'1119.180273540713px 763.9492328499646px',
							'1115.3883221224037px 761.4212652377582px',
							'1112.8603545101973px 761.4212652377582px',
							'1111.8156332010713px 761.4212652377582px',
							'1111.8156332010713px 761.4212652377582px',
							'1110.938583213163px 761.4212652377582px',
							'1109.8938619040368px 765.9225953227583px',
							'1105.9342391645096px 776.008670183704px',
							'1099.7690936612717px 786.0947450446496px',
							'1095.8094709217444px 796.1808199055953px',
							'1089.6443254185065px 806.2668947665409px',
							'1084.420718872876px 813.2059079061685px',
							'1080.9383145091224px 820.1449210457961px',
							'1076.1919263392656px 824.8655136277732px',
							'1073.6639587270593px 827.3676856520999px',
							'1072.399974920956px 829.8698576764266px',
							'1069.8720073087497px 832.3720297007532px',
							'1068.6080235026466px 834.8742017250798px',
							'1067.3440396965434px 837.3763737494065px',
							'1065.0353345813141px 839.6592832767561px',
							'1063.771350775211px 842.1614553010828px',
							'1063.771350775211px 843.4125413132462px',
							'1059.9793993569017px 847.1657993497362px',
							'1058.2381971750249px 851.8863919317133px',
							'1055.233011187045px 856.6069845136905px',
							'1052.227825199065px 861.3275770956675px',
							'1049.222639211085px 866.0481696776446px',
							'1047.9586554049818px 870.7687622596218px',
							'1046.217453223105px 875.4893548415989px',
							'1044.9534694170018px 877.9915268659256px',
							'1044.9534694170018px 879.242612878089px',
							'1044.9534694170018px 879.242612878089px',
							'1044.9534694170018px 878.8427812659543px',
							'1044.9534694170018px 875.9923688052522px',
							'1044.9534694170018px 865.5580535079312px',
							'1044.9534694170018px 853.4341272044927px',
							'1044.9534694170018px 843.1932788162691px',
							'1044.9534694170018px 838.8596200524868px',
							'1044.9534694170018px 830.0117334097647px',
							'1044.9534694170018px 822.2601592519279px',
							'1044.9534694170018px 814.8826211183462px',
							'1044.9534694170018px 807.2987182817271px',
							'1044.9534694170018px 797.4705992995781px',
							'1044.9534694170018px 792.1180148145494px',
							'1044.9534694170018px 786.0302560749504px',
							'1044.9534694170018px 781.7739840748071px',
							'1044.9534694170018px 777.878850304979px',
							'1044.9534694170018px 774.1126944745492px',
							'1044.9534694170018px 771.8297849471996px',
							'1044.9534694170018px 769.5855688016695px',
							'1044.9534694170018px 768.2828916137469px',
							'1044.9534694170018px 764.142699759062px',
							'1044.9534694170018px 761.4470608256379px',
							'1044.9534694170018px 759.0996623285891px',
							'1045.4306877927754px 758.1581233709817px',
							'1046.1529642534058px 755.7720314921135px',
							'1046.1787598412855px 754.5596388617696px',
							'1046.1787598412855px 753.3730418193054px',
							'1046.1787598412855px 751.1288256737753px',
							'1046.1787598412855px 750.2001845101076px',
							'1046.1787598412855px 747.77539924942px',
							'1047.2879701201107px 744.1124257705088px',
							'1047.4040502655691px 737.0573324854228px',
							'1049.6095730292798px 725.0365885335028px',
							'1051.1444105081193px 707.9341137692907px',
							'1051.6861178535921px 694.4559191021702px',
							'1053.2854443021308px 685.5177479018693px',
							'1053.4789112112283px 678.5400413804222px',
							'1053.4789112112283px 675.9088914166972px',
							'1053.4789112112283px 674.644907610594px',
							'1053.4789112112283px 672.6070561681012px',
							'1053.4789112112283px 671.4333569195768px',
							'1053.4789112112283px 670.2338620831728px',
							'1053.4789112112283px 669.1246518043475px',
							'1053.4789112112283px 669.1246518043475px',
							'1053.4789112112283px 667.2286760951929px',
							'1053.4789112112283px 664.971562155723px',
							'1053.4789112112283px 663.6559871738605px',
							'1053.4789112112283px 660.9603482404364px',
							'1053.4789112112283px 654.8467939129578px',
							'1053.4789112112283px 653.8407659856512px',
							'1053.4789112112283px 650.2551792703789px',
							'1053.4789112112283px 648.9138087006368px',
							'1053.4789112112283px 647.5853359248345px',
							'1053.4789112112283px 646.3600455005508px',
							'1053.4789112112283px 645.366915367184px',
							'1053.4789112112283px 645.366915367184px',
							'1053.4789112112283px 644.464069791396px',
							'1053.4789112112283px 644.464069791396px',
							'1053.4789112112283px 643.4967352459089px',
							'1053.4789112112283px 643.4967352459089px',
							'1053.4789112112283px 642.5938896701209px',
							'1053.4789112112283px 641.6910440943329px',
							'1053.4789112112283px 641.6910440943329px',
							'1053.4789112112283px 640.6979139609663px',
							'1053.4789112112283px 639.66609044578px',
							'1053.4789112112283px 638.5052889911955px',
							'1053.4789112112283px 636.4674375487026px',
							'1053.4789112112283px 635.3066360941181px',
							'1053.4789112112283px 632.7399751000922px',
							'1053.4789112112283px 630.1088251363673px',
							'1053.4789112112283px 628.8061479484446px',
							'1053.4789112112283px 626.8198876817111px',
							'1053.4789112112283px 624.4466935967827px',
							'1053.4789112112283px 624.4466935967827px',
							'1053.4789112112283px 621.944521572456px',
							'1053.4789112112283px 620.745026736052px',
							'1053.4789112112283px 619.764794396625px',
							'1053.4789112112283px 618.8232554390175px',
							'1053.4789112112283px 617.7527385420118px',
							'1053.4789112112283px 617.7527385420118px',
							'1053.4789112112283px 616.7338128207654px',
							'1053.4789112112283px 616.7338128207654px',
							'1053.4789112112283px 615.8180694510376px',
							'1053.4789112112283px 615.8180694510376px',
							'1053.4789112112283px 613.9220937418828px',
							'1053.4789112112283px 613.9220937418828px',
							'1053.4789112112283px 612.9934525782153px',
							'1053.4789112112283px 612.0648114145476px',
							'1053.4789112112283px 612.0648114145476px',
							'1053.4789112112283px 611.058783487241px',
							'1053.4789112112283px 610.014062178115px',
							'1053.4789112112283px 609.0596254265677px',
							'1053.4789112112283px 608.1180864689602px',
							'1053.4789112112283px 607.0475695719545px',
							'1053.4789112112283px 605.9641548810089px',
							'1053.4789112112283px 604.8807401900633px',
							'1053.4789112112283px 604.8807401900633px',
							'1053.4789112112283px 601.8497586142037px',
							'1053.4789112112283px 600.6115703959802px',
							'1053.4789112112283px 598.1351939595331px',
							'1053.4789112112283px 596.9228013291893px',
							'1053.4789112112283px 594.4593226866821px',
							'1053.4789112112283px 593.4403969654356px',
							'1053.4789112112283px 589.8935036319829px',
							'1053.4789112112283px 587.1204779349198px',
							'1053.4789112112283px 585.7533117772981px',
							'1053.4789112112283px 583.0705706378138px',
							'1053.4789112112283px 580.9940258135015px',
							'1053.4789112112283px 579.9493045043754px',
							'1053.4789112112283px 578.7111162861519px',
							'1053.4789112112283px 578.7111162861519px',
							'1053.4789112112283px 577.6148038012665px',
							'1053.4789112112283px 576.5829802860802px',
							'1053.4789112112283px 576.5829802860802px',
							'1053.040386217274px 576.0799663224269px',
							'1053.040386217274px 576.0799663224269px',
							'1051.324979623277px 576.0799663224269px',
							'1050.3189516959703px 576.0799663224269px',
							'1047.571721586787px 577.4471324800487px',
							'1047.571721586787px 577.4471324800487px',
							'1047.571721586787px 578.2467957043181px',
							'1036.6343923258125px 600.1085564323268px',
							'1013.5731367613997px 632.3917346637169px',
							'965.4901609537202px 703.7552285327863px',
							'952.8245273048088px 713.2351070785601px',
							'952.8245273048088px 713.2351070785601px',
							'874.8573629385473px 789.6932295538617px',
							'787.1394663537757px 853.3180470590343px',
							'732.6462869580015px 873.2580364900086px',
							'673.071376749935px 887.342427472301px',
							'635.3711250638172px 894.1395648785905px',
							'602.7526041899916px 894.1395648785905px',
							'581.2648794862379px 894.1395648785905px',
							'571.1530090374125px 894.1395648785905px',
							'564.1882003099053px 894.1395648785905px',
							'561.6602326976989px 894.1395648785905px',
							'556.9138445278422px 894.1395648785905px',
							'549.949035800335px 894.1395648785905px',
							'536.6901036301915px 898.5506104060117px',
							'512.6873091081712px 903.4388743092067px',
							'495.3139806712227px 908.3271382124016px',
							'472.85892142198173px 911.4870977276595px',
							'472.85892142198173px 911.4870977276595px',
							'551.4064865155356px 911.4870977276595px',
							'674.1418936469407px 911.4870977276595px',
							'752.9990057950504px 918.052074843032px',
							'819.8611695791199px 930.2017967343502px',
							'1111.0288677707417px 974.5315145055397px',
							'1393.735613137836px 1016.9394609796949px',
							'1501.470885917221px 1034.196709271185px',
							'1594.2318199324657px 1044.3859664836493px',
							'1659.9976712316497px 1053.1048751869732px',
							'1691.6230619721084px 1053.1048751869732px',
							'1718.1667219002748px 1055.7876163264575px',
							'1735.5142547493438px 1055.7876163264575px',
							'1748.7473913316076px 1057.9931390901681px',
							'1761.9805279138714px 1060.1986618538788px',
							'1779.3280607629401px 1062.881402993363px',
							'1805.8717206911067px 1068.724103648105px',
							'1878.4734027783998px 1084.2401497577184px',
							'1944.2392540775836px 1110.4355692495096px',
							'2010.0051053767677px 1136.6309887413006px',
							'2046.8927960446763px 1150.4574238447965px',
							'2184.525155176583px 1186.1198240884214px',
							'2237.225541214721px 1203.1062187071752px',
							'2287.9654625740054px 1222.7624566714735px',
							'2321.125690793304px 1233.4934212294104px',
							'2327.742259084436px 1235.698943993121px',
							'2330.2444311087625px 1238.2011160174477px',
							'2331.495517120926px 1239.452202029611px',
							'2330.579773751198px 1239.452202029611px',
							'2306.5769792291776px 1239.452202029611px',
							'2299.947513144106px 1239.452202029611px',
							'2282.574184707157px 1239.452202029611px',
							'2272.462314258332px 1239.452202029611px',
							'2265.4975055308246px 1241.180506417548px',
							'2260.751117360968px 1242.908810805485px',
							'2252.857667469793px 1246.365419581359px',
							'2245.8928587422856px 1249.822028357233px',
							'2243.3648911300793px 1252.3242003815596px',
							'2239.57293971177px 1253.575286393723px',
							'2239.57293971177px 1253.575286393723px',
							'2238.6056051662827px 1253.575286393723px',
							'2238.6056051662827px 1253.575286393723px',
							'2236.0518419661967px 1253.575286393723px',
							'2203.4333210923714px 1253.575286393723px',
							'2147.5471799510956px 1253.575286393723px',
							'1988.8011321396903px 1233.3515454960723px',
							'1733.2571408098738px 1198.5017062706565px',
							'1733.2571408098738px 1198.5017062706565px',
							'962.3173036445224px 1011.5610809067865px',
							'569.7342517040314px 899.0278287817854px',
							'78.66364523901224px 779.7619282201939px',
							'0px 744.7444176735604px',
							'0px 731.0985516852221px',
							'0px 729.8345678791189px',
							'0px 729.8345678791189px',
							'4.333658763782278px 729.8345678791189px',
							'17.566795346046018px 729.8345678791189px',
							'34.91432819511496px 729.8345678791189px',
							'52.261861044183895px 729.8345678791189px',
							'78.80552097235035px 729.8345678791189px',
							'110.43091171280912px 729.8345678791189px',
							'142.05630245326788px 732.9945273943769px',
							'173.68169319372666px 742.4744059401506px',
							'195.14362230960077px 750.5226293586034px',
							'205.22969717054644px 756.6619792739616px',
							'209.95028975252356px 759.6413696740619px',
							'214.6708823345007px 762.6207600741623px',
							'217.17305435882736px 763.8718460863256px',
							'217.17305435882736px 766.3740181106523px',
							'218.4241403709907px 770.1272761471423px',
							'218.4241403709907px 771.3783621593057px',
							'218.4241403709907px 774.8478687291195px',
							'218.4241403709907px 795.0200184510107px',
							'218.4241403709907px 795.0200184510107px',
							'261.69623903911435px 795.0200184510107px',
							'363.44693543041905px 795.0200184510107px',
							'567.3352620312234px 820.7124239791485px',
							'841.3875876646931px 872.0972350354241px',
							'841.3875876646931px 872.0972350354241px',
							'908.2497514487626px 884.2469569267423px',
							'1262.590844357663px 973.8479314267288px',
							'1321.3402957535804px 990.357107669709px',
							'1353.9330210395262px 996.6770267002247px',
							'1382.0115184465324px 1004.2480317429039px',
							'1392.0975933074778px 1006.4535545066145px',
							'1402.1836681684235px 1010.3873816582621px',
							'1415.4168047506873px 1014.7984271856834px',
							'1438.800505163596px 1029.6437880104256px',
							'1476.474961261834px 1050.048098023234px',
							'1498.336721989843px 1060.9725294902685px',
							'1520.1984827178517px 1071.8969609573028px',
							'1643.7206552795865px 1118.2258367891657px',
							'1672.5988159108617px 1126.467527116716px',
							'1820.1495785824966px 1158.7378075541662px',
							'1902.914722294374px 1173.531577203149px',
							'1950.5333775202198px 1185.4362410096105px',
							'2008.2896987827705px 1193.665033543221px',
							'2052.013220238788px 1197.3022114342525px',
							'2089.6876763370265px 1204.099348840542px',
							'2134.378532338531px 1211.3737046226051px',
							'2156.8077959998923px 1222.1046691805423px',
							'2178.2697251157665px 1232.8356337384791px',
							'2197.0489130921565px 1246.2493394359005px',
							'2217.8659525110393px 1259.4953738121042px',
							'2224.804965650667px 1264.693184769855px',
							'2227.3071376749936px 1267.1953567941816px',
							'2229.8093096993202px 1269.6975288185083px',
							'2231.0603957114836px 1270.9486148306717px',
							'2231.0603957114836px 1270.9486148306717px',
							'2231.9632412872716px 1271.8514604064596px',
							'2232.917678038819px 1273.102546418623px',
							'2232.917678038819px 1273.102546418623px',
							'2232.917678038819px 1273.9666986125915px',
							'2232.917678038819px 1273.9666986125915px',
							'2232.917678038819px 1274.8179530126201px',
							'2233.872114790366px 1275.7723897641674px',
							'2236.3742868146924px 1277.0234757763308px',
							'2236.3742868146924px 1277.0234757763308px',
							'2243.7905183300936px 1277.0234757763308px',
							'2257.0236549123574px 1270.3811118973192px',
							'2267.109729773303px 1266.421489157792px',
							'2271.8303223552803px 1263.416303169812px',
							'2274.332494379607px 1262.1523193637088px',
							'2274.332494379607px 1262.1523193637088px',
							'2274.332494379607px 1261.236575993981px',
							'2274.332494379607px 1261.236575993981px',
							'2275.235339955395px 1259.9725921878778px',
							'2277.7375119797216px 1257.4446245756715px',
							'2287.8235868406673px 1251.7437996542674px',
							'2314.367246768834px 1242.7153438963876px',
							'2320.9838150599658px 1240.4969233387371px',
							'2400.601897050525px 1232.242335217247px',
							'2437.489587718434px 1232.242335217247px',
							'2543.7029208129193px 1232.242335217247px',
							'2594.442842172203px 1232.242335217247px',
							'2626.068232912662px 1232.242335217247px',
							'2626.068232912662px 1232.242335217247px',
							'2642.7708760647397px 1232.242335217247px',
							'2645.2730480890664px 1233.4934212294104px',
							'2647.775220113393px 1234.7445072415737px',
							'2647.775220113393px 1237.2466792659004px',
							'2647.775220113393px 1238.4977652780638px',
							'2612.4610603061433px 1269.1687192669754px',
							'2571.8717027775037px 1295.1448762617892px',
							'2517.37852338173px 1318.7220435837953px',
							'2466.6128064345658px 1330.1236934266035px',
							'2434.9616201062277px 1336.4436124571191px',
							'2408.3921645901814px 1336.4436124571191px',
							'2381.822709074135px 1336.4436124571191px',
							'2368.5637769039918px 1336.4436124571191px',
							'2355.3048447338483px 1332.0196691357582px',
							'2348.6753786487766px 1327.5957258143972px',
							'2341.71056992127px 1322.3721192687667px',
							'2337.9186185029603px 1318.5801678504572px',
							'2335.390650890754px 1317.316184044354px',
							'2333.0819457755247px 1317.316184044354px',
							'2330.5539781633183px 1317.316184044354px',
							'2329.289994357215px 1316.0522002382509px',
							'2326.981289241986px 1316.0522002382509px',
							'2326.981289241986px 1316.0522002382509px',
							'2326.013954696499px 1316.0522002382509px',
							'2325.098211326771px 1316.0522002382509px',
							'2323.834227520668px 1316.0522002382509px',
							'2317.823855544708px 1316.0522002382509px',
							'2316.7791342355818px 1316.0522002382509px',
							'2314.2511666233754px 1314.7882164321477px',
							'2312.9871828172722px 1314.7882164321477px',
							'2310.678477702043px 1314.7882164321477px',
							'2308.1505100898366px 1313.5242326260445px',
							'2305.6225424776303px 1313.5242326260445px',
							'2303.094574865424px 1312.2602488199414px',
							'2301.830591059321px 1310.9962650138382px',
							'2299.5218859440915px 1309.951543704712px',
							'2294.7754977742347px 1306.946357716732px',
							'2293.5115139681316px 1306.946357716732px',
							'2286.5467052406243px 1303.4639533529785px',
							'2281.8003170707675px 1300.4587673649985px',
							'2273.0943061613834px 1293.4939586374912px',
							'2268.3479179915266px 1291.7527564556144px',
							'2263.60152982167px 1288.7475704676344px',
							'2261.0735622094635px 1286.219602855428px',
							'2258.545594597257px 1284.955619049325px',
							'2257.281610791154px 1283.6916352432218px',
							'2257.281610791154px 1283.6916352432218px',
							'2256.4174585971855px 1283.6916352432218px',
							'2256.4174585971855px 1282.7243006977346px',
							'2256.4174585971855px 1282.7243006977346px',
							'2255.540408609277px 1281.8472507098263px',
						],
						{
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
				}}
			/>
		</>
	);

	return (
		<AbsoluteFill
			showInTimeline={false}
			style={{backgroundColor: '#111518', overflow: 'hidden'}}
		>
			{HtmlInCanvas.isSupported() ? (
				<HtmlInCanvas
					name="Canvas Capture"
					width={2542}
					height={1440}
					effects={[
						radialProgressiveBlur({
							center: [0.11, 0.72],
							width: 0.55,
							height: 1.1,
							start: 0.65,
							startBlur: 0,
							endBlur: 24,
						}),
					]}
					durationInFrames={1189}
					premountFor={fps}
					style={{
						translate: '-100px -480px',
						scale: 1.1,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</HtmlInCanvas>
			) : (
				<AbsoluteFill
					name="Canvas Capture"
					durationInFrames={1189}
					premountFor={fps}
					style={{
						width: 2542,
						height: 1440,
						translate: '-100px -480px',
						scale: 1.1,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</AbsoluteFill>
			)}
		</AbsoluteFill>
	);
};

export const BasicsVirtualizedTimelinePreview = Interactive.withSchema({
	Component: BasicsVirtualizedTimelineInner,
	componentName: 'BasicsVirtualizedTimelinePreview',
	schema: {},
	wrapInSequence: true,
	layout: 'absolute-fill',
});

export const BasicsVirtualizedTimelineComposition = () => {
	return (
		<Composition
			id="BasicsVirtualizedTimeline"
			component={BasicsVirtualizedTimelinePreview}
			width={1920}
			height={1080}
			defaultProps={{captureStyle: null}}
			fps={60}
			durationInFrames={1189}
		/>
	);
};
